import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import * as XLSX from 'xlsx';
import dictionary from '../../../Mobx/Dictionary';
import groups from '../../../Mobx/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    Alert,
} from '@mui/material';

const ImportFromExcel = observer(() => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [errorRows, setErrorRows] = useState([]);
    const [successCount, setSuccessCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);
    const [stage, setStage] = useState('validation'); // 'validation' | 'import'
    const [fileName, setFileName] = useState('');
    const [abortController, setAbortController] = useState(null);

    const dictionaryType = dictionary.current.dictionaryType;

    const extractErrorMessage = (error) => {
        if (error?.response?.data) {
            return typeof error.response.data === 'object'
                ? JSON.stringify(error.response.data)
                : String(error.response.data);
        }
        if (error?.message) return error.message;
        return String(error);
    };

    const requiredFieldsByType = {
        dictionary_eng_to_heb: ['original'],
        dictionary_heb_to_eng: ['original', 'value'],
    };

    // "list" (level group A-E) is case-insensitive - the server also saves it in upper case
    const normalizeListCell = (field, value) => (field === 'list' && typeof value === 'string' ? value.toUpperCase() : value);

    const validateExcelEntry = (entry, dictionaryType) => {
        const errors = [];

        const requiredFields = requiredFieldsByType[dictionaryType] || [];

        for (const field of dictionary.current.fields) {
            if (["_id", "createdAt", "updatedAt"].includes(field)) continue;

            let value = entry[field];

            // בדיקת שדות חובה
            if (requiredFields.includes(field)) {
                if (
                    value === undefined ||
                    value === null ||
                    (typeof value === 'string' && value.trim() === '')
                ) {
                    errors.push({ field, message: `שדה חובה ריק: "${field}"` });
                }
            }

            if (["olami", "latin"].includes(field)) {
                if (value === true) value = "true";
                if (value === false) value = null;
            }

            if (field === 'categories') {
                const names = (typeof value === 'string' || typeof value === 'number')
                    ? String(value).split(',').map(n => n.trim())
                    : [];

                const uniqueNames = new Set();
                for (const name of names) {
                    if (!name) continue;

                    if (uniqueNames.has(name)) {
                        errors.push({ field, message: `קטגוריה כפולה: "${name}"` });
                    } else {
                        uniqueNames.add(name);
                        const exists = groups.categories[dictionaryType].some(cat => cat.name === name);
                        if (!exists && name !== '') {
                            errors.push({ field, message: `קטגוריה לא קיימת: "${name}". (כדי להוסיף מספר קטגוריות, ניתן לכתוב אותם מופרדים בפסיקים, לדוגמה: "Colors, Sport".)` });
                        }
                    }
                }
            } else if (["pos", "latin", "band", "priority", "olami", "imageType", "list"].includes(field)) {
                const allowed = groups[field]?.[dictionaryType] || [];
                const trimmed = normalizeListCell(field, typeof value === 'string' ? value.trim() : value);
                if (trimmed && !allowed.includes(trimmed)) {
                    errors.push({ field, message: `ערך לא חוקי: "${trimmed}" בשדה "${field}"` });
                }
            }
        }

        return errors;
    };

    const normalizeDictionaryEntry = (rawEntry, dictionaryType) => {
        const cleanedEntry = {};

        for (const field of dictionary.current.fields) {
            let value = rawEntry[field];

            if (["olami", "latin"].includes(field)) {
                if (value === true) value = "true";
                if (value === false) value = null;
            }

            if (["_id", "createdAt", "updatedAt"].includes(field)) continue;

            if (field === 'categories') {
                const names = (typeof value === 'string' || typeof value === 'number')
                    ? String(value).split(',').map(name => name.trim())
                    : [];

                const uniqueNames = [...new Set(names.filter(Boolean))];

                const categories = uniqueNames
                    .map(name => {
                        const match = groups.categories[dictionaryType].find(cat => cat.name === name);
                        return match ? { label: match.name, value: match._id } : null;
                    })
                    .filter(Boolean);

                if (categories.length > 0) {
                    cleanedEntry.categories = categories;
                }
            } else if (["pos", "latin", "band", "priority", "olami", "imageType", "list"].includes(field)) {
                const allowedValues = groups[field]?.[dictionaryType] || [];
                const trimmed = normalizeListCell(field, typeof value === 'string' ? value.trim() : value);
                cleanedEntry[field] = allowedValues.includes(trimmed) ? trimmed : '';
            } else {
                cleanedEntry[field] = typeof value === 'string' ? value.trim() : value;
            }
        }

        return cleanedEntry;
    };

    const handleOpen = () => {
        setPreviewData([]);
        setErrorRows([]);
        setSuccessCount(0);
        setErrorCount(0);
        setStage('validation');
        setIsDialogOpen(true);
        setFileName('');
    };

    const handleClose = () => {
        if (!isLoading) {
            setIsDialogOpen(false);
            setPreviewData([]);
            setErrorRows([]);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSuccessCount(0);
        setErrorCount(0);
        setFileName(file.name);

        try {
            const data = await readExcel(file);

            if (data.length > 500) {
                setPreviewData([]);
                setErrorRows([{
                    index: 0,
                    field: 'קובץ',
                    message: `הקובץ מכיל ${data.length}. ניתן לייבא עד 500 שורות בכל פעם.`
                }]);
                setErrorCount(1);
                setSuccessCount(0);
                setStage('validation');
                return;
            }

            // בדיקת תקינות הכותרות
            const expectedFields = dictionary.current.fields.filter(
                f => !["_id", "createdAt", "updatedAt"].includes(f)
            );

            const headersInFile = Object.keys(data[0] || {});
            const missingFields = expectedFields.filter(field => !headersInFile.includes(field));
            const unknownFields = headersInFile.filter(field => !expectedFields.includes(field));

            if (missingFields.length > 0 || unknownFields.length > 0) {
                const messages = [];

                if (missingFields.length > 0) {
                    messages.push(`שדות חסרים בקובץ האקסל: ${missingFields.join(', ')}`);
                }

                if (unknownFields.length > 0) {
                    messages.push(`שדות מיותרים בקובץ האקסל: ${unknownFields.join(', ')}`);
                }

                setPreviewData([]);
                setErrorRows([{
                    index: 0,
                    field: 'Header',
                    message: messages.join('\n')
                }]);
                setErrorCount(1);
                setSuccessCount(0);
                setStage('validation');
                return;
            }

            // בדיקת תקינות הנתונים
            const validationErrors = [];
            for (const [i, row] of data.entries()) {
                const rowErrors = validateExcelEntry(row, dictionaryType);
                if (rowErrors.length > 0) {
                    rowErrors.forEach(e =>
                        validationErrors.push({ index: i + 1, field: e.field, message: e.message, entry: row }));
                }
            }

            setPreviewData(data);
            setErrorRows(validationErrors);
            setErrorCount(validationErrors.length);
            setSuccessCount(data.length - validationErrors.length);
            setStage('validation');

        } catch (error) {
            setPreviewData([]);
            setErrorRows([{ index: 0, error: extractErrorMessage(error) }]);
            setErrorCount(1);
            setSuccessCount(0);
            setStage('validation');
        } finally {
            event.target.value = '';
        }
    };

    const handleImport = async () => {
        if (errorRows.length > 0) return;

        const controller = new AbortController();
        setSuccessCount(0);
        setAbortController(controller);
        setStage('import');
        setIsLoading(true);

        // איפוס סטטוסים של שורות קודמות
        const resetRows = previewData.map(row => {
            const newRow = { ...row };
            delete newRow.__status;
            return newRow;
        });
        setPreviewData(resetRows);

        let success = 0;
        let failed = 0;
        const errors = [];
        const updatedRows = [...resetRows];

        for (let i = 0; i < updatedRows.length; i++) {
            if (controller.signal.aborted) break;

            const rawEntry = updatedRows[i];
            try {
                const entry = normalizeDictionaryEntry(rawEntry, dictionaryType);
                await dictionary.create(entry);
                updatedRows[i] = { ...rawEntry, __status: 'success' };
                success++;
            } catch (error) {
                updatedRows[i] = { ...rawEntry, __status: 'error' };
                failed++;
                errors.push({
                    index: i + 1,
                    error: extractErrorMessage(error),
                    entry: rawEntry
                });
            }

            setPreviewData([...updatedRows]);
            setSuccessCount(success);
            setErrorCount(failed);
            setErrorRows([...errors]);
        }

        if (controller.signal.aborted) {
            for (let j = updatedRows.findIndex(r => !r.__status); j < updatedRows.length; j++) {
                updatedRows[j] = { ...updatedRows[j], __status: 'aborted' };
            }
            setPreviewData([...updatedRows]);
        }

        setIsLoading(false);
        setAbortController(null);
    };

    const handleCancelImport = () => {
        if (abortController) {
            abortController.abort();
            setIsLoading(false);
            setAbortController(null);
        }
    };

    const readExcel = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                resolve(json);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    const handleResetFile = () => {
        setPreviewData([]);
        setErrorRows([]);
        setSuccessCount(0);
        setErrorCount(0);
        setFileName('');
        setStage('validation');
    };

    const tableContainerRef = useRef();

    useEffect(() => {
        const el = tableContainerRef.current;
        if (el) {
            el.scrollTop = 0;
            el.scrollLeft = 100000;
        }
    }, [previewData]);

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                size="large"
                fullWidth
                onClick={handleOpen}
                startIcon={<UploadFileIcon />}
            >
                יבוא
            </Button>

            <Dialog
                open={isDialogOpen}
                onClose={handleClose}
                fullScreen
                PaperProps={{
                    sx: {
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '1px solid #ccc',
                        p: 1,
                        minHeight: 'unset',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        {/* צד שמאל */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ ml: 1.5 }}>
                                יבוא למילון: {dictionaryType === 'dictionary_heb_to_eng' ? 'עברי-אנגלי' : 'אנגלי-עברי'}
                            </Typography>

                            <label htmlFor="excel-upload">
                                <input
                                    id="excel-upload"
                                    type="file"
                                    accept=".xlsx, .xls"
                                    onChange={handleFileUpload}
                                    disabled={isLoading}
                                    style={{ display: 'none' }}
                                />
                                <Button variant="outlined" size="small" component="span" disabled={isLoading}>
                                    טען קובץ אקסל
                                </Button>
                            </label>

                            <Button
                                variant="text"
                                size="small"
                                onClick={handleResetFile}
                                disabled={isLoading || !fileName}
                                color="secondary"
                            >
                                בטל קובץ
                            </Button>

                            {fileName && (
                                <Typography variant="caption" color="text.secondary">
                                    שם הקובץ: <b>{fileName}</b>
                                </Typography>
                            )}

                            <Box sx={{ height: 32 }}>
                                {(successCount + errorCount > 0 || stage === 'import') && (
                                    <Alert
                                        severity={
                                            stage === 'import'
                                                ? (isLoading ? 'info' : errorCount === 0 ? 'success' : 'warning')
                                                : errorCount === 0
                                                    ? 'success'
                                                    : 'warning'
                                        }
                                        sx={(theme) => {
                                            const baseStyles = {
                                                p: 0.5,
                                                fontSize: '0.875rem',
                                                height: 32,
                                                alignItems: 'center',
                                                display: 'flex',
                                                gap: 0,
                                                pl: 1.5,
                                                pr: 2.5,
                                            };

                                            if (isLoading) {
                                                return {
                                                    ...baseStyles,
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#0d47a1',
                                                    border: '1px solid #64b5f6',
                                                };
                                            }

                                            if (errorCount === 0) {
                                                return {
                                                    ...baseStyles,
                                                    backgroundColor: '#e6ffe6',
                                                    color: '#1b5e20',
                                                    border: '1px solid #81c784',
                                                };
                                            }

                                            return {
                                                ...baseStyles,
                                                backgroundColor: '#fff8e1',
                                                color: '#e65100',
                                                border: '1px solid #ffb74d',
                                            };
                                        }}
                                        iconMapping={{
                                            info: isLoading ? <CircularProgress size={14} /> : undefined,
                                            success: <CheckCircleIcon fontSize="small" sx={{ color: '#43a047' }} />,
                                        }}
                                    >
                                        {stage === 'validation' && errorCount === 0
                                            ? `הקובץ תקין. מספר שורות: ${successCount}`
                                            : stage === 'validation' && errorCount > 0
                                                ? `הקובץ לא תקין. מספר שגיאות: ${errorCount}`
                                                : isLoading
                                                    ? `ייבוא מתבצע: ${successCount} הצליחו, ${errorCount} נכשלו`
                                                    : `ייבוא הסתיים: ${successCount} הצליחו, ${errorCount} נכשלו`}
                                    </Alert>
                                )}
                            </Box>
                        </Box>

                        {/* צד ימין */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isLoading ? (
                                <Button
                                    onClick={handleCancelImport}
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                >
                                    בטל ייבוא
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleImport}
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    disabled={previewData.length === 0 || errorRows.length > 0}
                                >
                                    ייבוא
                                </Button>
                            )}
                            <Button onClick={handleClose} size="small" disabled={isLoading}>
                                סגור
                            </Button>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        border: '2px solid #ccc',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0, // מבטל רווחים פנימיים
                        direction: 'rtl',
                    }}
                >
                    {(previewData.length > 0 || errorRows.length > 0) ? (
                        <TableContainer
                            ref={tableContainerRef}
                            component={Paper}
                            sx={{
                                mt: 0,
                                flex: 1,
                                boxShadow: 'none',
                                borderRadius: 0,
                                padding: 0,
                                margin: 0,
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                sx={{
                                    minWidth: '100%',
                                    borderCollapse: 'collapse',
                                    direction: 'ltr',
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {stage === 'validation' && errorRows.length > 0 ? (
                                            ['שורה', 'שדה', 'שגיאה'].map((label, idx) => (
                                                <TableCell
                                                    key={idx}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#f0f0f0',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    {label}
                                                </TableCell>
                                            ))
                                        ) : (
                                            <>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#f0f0f0',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    שורה
                                                </TableCell>
                                                {Object.keys(previewData[0] || {}).filter(key => key !== '__status').map((key) => (
                                                    <TableCell
                                                        key={key}
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            backgroundColor: '#f0f0f0',
                                                        }}
                                                    >
                                                        {key}
                                                    </TableCell>
                                                ))}
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stage === 'validation' && errorRows.length > 0 ? (
                                        errorRows.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#fafafa',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {row.index}
                                                </TableCell>
                                                <TableCell>{row.field || '-'}</TableCell>
                                                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {row.message || row.error}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        previewData.map((row, idx) => {
                                            const rowIndex = idx + 1;
                                            const isError = row.__status === 'error';
                                            const matchingErrors = errorRows.filter(e => e.index === rowIndex);

                                            return (
                                                <React.Fragment key={idx}>
                                                    <TableRow
                                                        sx={{
                                                            backgroundColor:
                                                                row.__status === 'success'
                                                                    ? '#e6ffe6'
                                                                    : row.__status === 'error'
                                                                        ? '#ffe6e6'
                                                                        : 'inherit'
                                                        }}
                                                    >
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                backgroundColor: '#fafafa',
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            {rowIndex + 1}
                                                        </TableCell>
                                                        {Object.keys(previewData[0] || {}).filter(key => key !== '__status').map((key) => (

                                                            <TableCell key={key}>
                                                                {Array.isArray(row[key])
                                                                    ? row[key].join(', ')
                                                                    : String(row[key] || '')}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>

                                                    {isError && matchingErrors.length > 0 && (
                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell colSpan={Object.keys(previewData[0] || {}).length}>
                                                                {matchingErrors.map((e, i) => (
                                                                    <Typography
                                                                        key={i}
                                                                        variant="body2"
                                                                        sx={{ color: 'red', whiteSpace: 'pre-wrap' }}
                                                                    >
                                                                        {e.field ? `[${e.field}] ` : ''}
                                                                        {e.message || e.error}
                                                                    </Typography>
                                                                ))}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'gray', direction: 'ltr' }}>
                            אין נתונים להצגה. טען קובץ אקסל.
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
});

export default ImportFromExcel;